/* ============================================================
   ASSAF CRIMINAL LAW — motion controller
   Reveals, word-split headings, cursor glow, scroll filament,
   counters, tilt cards, sticky process, particles, call bar.
   Every effect is gated on prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isDesktop = window.matchMedia('(min-width: 990px)').matches;

  /* ---------- Page-load sequence (homepage hero) ---------- */
  if (!reduceMotion && document.querySelector('.assaf-hero')) {
    document.body.classList.add('assaf-loading');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        document.body.classList.remove('assaf-loading');
      });
    });
  }

  /* ---------- Word-by-word heading reveal ---------- */
  document.querySelectorAll('[data-assaf-words]').forEach(function (el) {
    if (reduceMotion || el.children.length) return;
    var words = el.textContent.trim().split(/\s+/);
    el.textContent = '';
    words.forEach(function (word, i) {
      var span = document.createElement('span');
      span.className = 'assaf-word';
      span.style.setProperty('--word-i', i);
      span.textContent = word;
      el.appendChild(span);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
    });
  });

  /* ---------- Reveal observer + sibling stagger ---------- */
  document.querySelectorAll('[data-assaf-stagger]').forEach(function (group) {
    var i = 0;
    Array.prototype.forEach.call(group.children, function (child) {
      if (!child.classList.contains('assaf-reveal')) return;
      if (!child.style.getPropertyValue('--reveal-delay')) {
        child.style.setProperty('--reveal-delay', Math.min(i * 90, 540) + 'ms');
      }
      i++;
    });
  });

  var revealTargets = document.querySelectorAll('.assaf-reveal, .assaf-divider, [data-assaf-words]');
  if (reduceMotion) {
    revealTargets.forEach(function (el) { el.classList.add('is-visible'); });
  } else if (revealTargets.length) {
    var revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -8% 0px' }
    );
    revealTargets.forEach(function (el) { revealObserver.observe(el); });
  }

  /* ---------- Scroll filament (scaleY = scroll progress) ---------- */
  var filament = document.querySelector('.assaf-filament');
  if (filament && !reduceMotion) {
    var filamentTicking = false;
    var updateFilament = function () {
      filamentTicking = false;
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var p = max > 0 ? window.scrollY / max : 0;
      filament.style.setProperty('--scroll-progress', p.toFixed(4));
    };
    window.addEventListener('scroll', function () {
      if (!filamentTicking) {
        filamentTicking = true;
        requestAnimationFrame(updateFilament);
      }
    }, { passive: true });
    updateFilament();
  }

  /* ---------- Cursor-follow gold glow (desktop only) ---------- */
  var glow = document.querySelector('.assaf-cursor-glow');
  if (glow && !reduceMotion && isDesktop && window.matchMedia('(pointer: fine)').matches) {
    var gx = window.innerWidth / 2, gy = window.innerHeight / 3;
    var tx = gx, ty = gy;
    document.addEventListener('pointermove', function (e) {
      tx = e.clientX;
      ty = e.clientY;
    }, { passive: true });
    (function glowFrame() {
      gx += (tx - gx) * 0.12; /* lag is intentional: snap-to-pointer feels dead */
      gy += (ty - gy) * 0.12;
      glow.style.background =
        'radial-gradient(340px circle at ' + gx.toFixed(1) + 'px ' + gy.toFixed(1) + 'px, rgba(184,146,73,0.09), transparent 70%)';
      requestAnimationFrame(glowFrame);
    })();
  }

  /* ---------- Ambient particles (gold dust, capped for perf) ---------- */
  document.querySelectorAll('.assaf-hero__particles').forEach(function (canvas) {
    if (!canvas.getContext || reduceMotion) return;
    var ctx = canvas.getContext('2d');
    var particles = [];
    var COUNT = isDesktop ? 42 : 20;
    var sizeCanvas = function () {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    sizeCanvas();
    window.addEventListener('resize', sizeCanvas, { passive: true });
    for (var i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 1.8 + 0.4,
        vx: (Math.random() - 0.5) * 0.18,
        vy: -(Math.random() * 0.22 + 0.05),
        a: Math.random() * 0.5 + 0.15,
        tw: Math.random() * 0.02
      });
    }
    var heroVisible = true;
    new IntersectionObserver(function (entries) {
      heroVisible = entries[0].isIntersecting;
    }).observe(canvas);
    (function particleFrame() {
      if (heroVisible) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        particles.forEach(function (p) {
          p.x += p.vx;
          p.y += p.vy;
          p.a += p.tw;
          if (p.a > 0.65 || p.a < 0.1) p.tw *= -1;
          if (p.y < -4) { p.y = canvas.height + 4; p.x = Math.random() * canvas.width; }
          if (p.x < -4) p.x = canvas.width + 4;
          if (p.x > canvas.width + 4) p.x = -4;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(227,189,101,' + Math.max(p.a, 0.05).toFixed(3) + ')';
          ctx.fill();
        });
      }
      requestAnimationFrame(particleFrame);
    })();
  });

  /* ---------- Animated counters (count-up in view) ---------- */
  var counters = document.querySelectorAll('[data-assaf-count]');
  if (counters.length && !reduceMotion) {
    var counterObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        counterObserver.unobserve(entry.target);
        var el = entry.target;
        var target = parseFloat(el.getAttribute('data-assaf-count'));
        if (isNaN(target)) return; /* TODO placeholders render an em dash, no count */
        var suffix = el.getAttribute('data-assaf-suffix') || '';
        var start = null;
        var DURATION = 1400;
        var step = function (t) {
          if (!start) start = t;
          var p = Math.min((t - start) / DURATION, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(target * eased) + suffix;
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    }, { threshold: 0.5 });
    counters.forEach(function (el) { counterObserver.observe(el); });
  }

  /* ---------- Tilt cards (services grid) ---------- */
  if (!reduceMotion && isDesktop && window.matchMedia('(pointer: fine)').matches) {
    document.querySelectorAll('.assaf-tilt').forEach(function (card) {
      var raf = null;
      card.addEventListener('pointermove', function (e) {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = null;
          var rect = card.getBoundingClientRect();
          var px = (e.clientX - rect.left) / rect.width - 0.5;
          var py = (e.clientY - rect.top) / rect.height - 0.5;
          card.style.transform =
            'perspective(800px) rotateY(' + (px * 6).toFixed(2) + 'deg) rotateX(' + (-py * 6).toFixed(2) + 'deg)';
        });
      });
      card.addEventListener('pointerleave', function () {
        card.style.transform = '';
      });
    });
  }

  /* ---------- Sticky process: active step drives the big number ---------- */
  var processVisual = document.querySelector('.assaf-process__visual');
  var steps = document.querySelectorAll('.assaf-step');
  if (processVisual && steps.length && !reduceMotion) {
    var numEl = processVisual.querySelector('.assaf-process__num');
    var setStep = function (n) {
      if (numEl.textContent === n) return;
      processVisual.classList.add('is-switching');
      setTimeout(function () {
        numEl.textContent = n;
        processVisual.classList.remove('is-switching');
      }, 200);
    };
    var stepObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) setStep(entry.target.getAttribute('data-step'));
      });
    }, { rootMargin: '-40% 0px -40% 0px' });
    steps.forEach(function (s) { stepObserver.observe(s); });
  }

  /* ---------- Header: mobile menu + sticky call bar after hero ---------- */
  var header = document.querySelector('.assaf-header');
  if (header) {
    var burger = header.querySelector('.assaf-burger');
    if (burger) {
      burger.addEventListener('click', function () {
        /* panel top = actual header bottom (announce bar height varies with scroll) */
        document.body.style.setProperty('--assaf-header-h', header.getBoundingClientRect().bottom + 'px');
        var open = header.classList.toggle('is-open');
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.body.classList.toggle('assaf-noscroll', open);
      });
      header.querySelectorAll('.assaf-nav a').forEach(function (a) {
        a.addEventListener('click', function () {
          header.classList.remove('is-open');
          burger.setAttribute('aria-expanded', 'false');
          document.body.classList.remove('assaf-noscroll');
        });
      });
    }
    document.body.style.setProperty('--assaf-header-h', header.offsetHeight + 'px');
  }

  var callbar = document.querySelector('.assaf-callbar');
  if (callbar) {
    if (reduceMotion) {
      callbar.classList.add('is-shown');
    } else {
      var hero = document.querySelector('.assaf-hero, .assaf-page-hero');
      if (hero) {
        new IntersectionObserver(function (entries) {
          callbar.classList.toggle('is-shown', !entries[0].isIntersecting);
        }).observe(hero);
      } else {
        callbar.classList.add('is-shown');
      }
    }
  }
})();
